import { useApolloClient } from "@apollo/client";
import {
  Text,
  useMantineTheme,
  Title,
  MantineTheme,
  Button,
} from "@mantine/core";
import {
  GetTimeClockEntriesPageQuery,
  GetTimeClockEntriesPageQueryVariables,
  useDeleteTimeClockEntryMutation,
  useGetEmployeesNameQuery,
} from "src/generated/graphql";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "src/api/AppContext";
import {
  ROUTE_NAMES,
  DEFAULT_ORDER,
  EMPLOYEE_ROLE_OBJ,
} from "src/utils/constants";
import { useModals } from "@mantine/modals";
import Select from "src/components/Select";
import { GetTimeClockEntriesPage } from "src/api/graphql/queries/Clock";
import TimeClockDetail from "../TimeClockDetail";
import CreateTimeLogForm from "./CreateTimeLog";
import { getDateDiff } from "src/utils/dateHelper";
import useCustomModal from "src/hooks/useCustomModal";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import useWritePermission from "src/hooks/usePermission";

export type TimeClock = ArrayElement<
  GetTimeClockEntriesPageQuery["timeClockEntryPage"]["items"]
>;

const TimeClockList = () => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.locTimeclock);
  const { DeleteRowIcon, refetchTableData, setrefetchTableData } =
    useCustomModal();
  const modals = useModals();
  const { setCurrentPageTitle, currentLocation, currentOrganization } =
    useContext(AppContext);
  const { data } = useGetEmployeesNameQuery({
    variables: { orgId: currentOrganization?.id || "" },
    skip: !currentOrganization,
  });
  const theme = useMantineTheme();
  const { query } = useApolloClient();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );

  const getEmployees = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<TimeClock>> => {
    let { data } = await query<
      GetTimeClockEntriesPageQuery,
      GetTimeClockEntriesPageQueryVariables
    >({
      query: GetTimeClockEntriesPage,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy: fetchParams.orderBy || DEFAULT_ORDER.name,
          filters: fetchParams.filters,
        },
        locationId: currentLocation!.id,
        employeeId: selectedEmployeeId ? selectedEmployeeId : "",
      },
    });

    if (data.timeClockEntryPage) {
      return {
        rowCount: data.timeClockEntryPage.totalRows,
        rowData: data.timeClockEntryPage.items as TimeClock[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  useEffect(() => {
    if (selectedEmployeeId) {
      const initParams = {
        page: 1,
        count: 15,
        filters: [],
      };
      getEmployees(initParams);
    }
  }, [selectedEmployeeId]);

  const [deleteTimeClockEntry] = useDeleteTimeClockEntryMutation({
    onCompleted: () => {
      setrefetchTableData(!refetchTableData);
    },
  });

  const onDelete = (clockId: string | undefined) => {
    clockId &&
      deleteTimeClockEntry({ variables: { timeClockEntryId: clockId } });
  };

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.TIME_CLOCK);
    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleModal = (clockId: string): void => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Update Time Clock Entry",
      children: (
        <TimeClockDetail
          clockId={clockId}
          setrefetchTableData={setrefetchTableData}
          refetchTableData={refetchTableData}
          isAllowWrite={isAllowWrite}
        />
      ),
    });
  };

  const CreateTimeLogModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Time Clock Entry",
      children: (
        <CreateTimeLogForm
          handleClose={() => {
            modals.closeAll();
          }}
          stationsEnabled={!!currentLocation?.stationsEnabled}
          onRefetch={() => {
            setrefetchTableData(!refetchTableData);
          }}
        />
      ),
    });
  }, [currentLocation, refetchTableData]);

  const ShowTable = useCallback(
    ({ theme }: { theme: MantineTheme }) => {
      return (
        <div>
          <ScrollableDataGrid<TimeClock>
            columnConfigs={[
              {
                field: "name",
                name: "Name",
                filter: {
                  field: "name",
                  type: "text",
                  parentRelationField: "employee.user",
                },
                sortable: true,
                cellRenderer: (rowItem) => (
                  <Text
                    weight={500}
                    onClick={() => handleModal(rowItem.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {`${rowItem.employee.user.firstName} ${rowItem.employee.user.lastName}`}
                  </Text>
                ),
              },
              {
                field: "startedAt",
                name: "Started At",
                filter: {
                  field: "startedAt",
                  type: "dateRange",
                },
                sortable: true,
                valueParser: (rowItem) => {
                  return rowItem.startedAt
                    ? `${new Date(
                        rowItem.startedAt
                      ).toDateString()}, ${new Date(
                        rowItem.startedAt
                      ).toLocaleTimeString()}`
                    : "N/A";
                },
              },
              {
                field: "endedAt",
                name: "Ended At",
                filter: {
                  field: "endedAt",
                  type: "dateRange",
                },
                sortable: true,
                valueParser: (rowItem) => {
                  return rowItem.endedAt
                    ? `${new Date(rowItem.endedAt).toDateString()}, ${new Date(
                        rowItem.endedAt
                      ).toLocaleTimeString()}`
                    : "N/A";
                },
              },
              {
                field: "duration",
                name: "Duration",
                sortable: false,
                cellRenderer: (rowItem) => (
                  <Text
                    weight={500}
                    onClick={() => handleModal(rowItem.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {!rowItem.endedAt
                      ? "Currently Active"
                      : `${getDateDiff(
                          rowItem.startedAt,
                          rowItem.endedAt
                        )} minutes`}
                  </Text>
                ),
              },
              {
                field: "timeclockentry_createdDate",
                name: "Created",
                filter: {
                  field: "createdDate",
                  type: "dateRange",
                },
                sortable: true,
                valueParser: (rowItem) => {
                  return new Date(rowItem.createdDate).toDateString();
                },
              },
              {
                field: "editedBy",
                name: "Modified By",
                filter: {
                  field: "editedBy",
                  type: "text",
                },
                sortable: true,
                cellRenderer: (rowItem) => (
                  <Text
                    weight={500}
                    onClick={() => handleModal(rowItem.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {rowItem.editedBy === null &&
                      rowItem.editedAt &&
                      "Sindro Admin"}
                    {rowItem.editedBy?.user.firstName}{" "}
                    {rowItem.editedBy?.user.lastName}
                  </Text>
                ),
              },
              {
                field: "editedAt",
                name: "Modified Date",
                filter: {
                  field: "editedAt",
                  type: "dateRange",
                },
                sortable: true,
                valueParser: (rowItem) => {
                  return rowItem.editedAt
                    ? `${new Date(rowItem.editedAt).toDateString()}, ${new Date(
                        rowItem.editedAt
                      ).toLocaleTimeString()}`
                    : "";
                },
              },
              ...(isAllowWrite
                ? [
                    {
                      field: "",
                      name: "",
                      cellRenderer: ({ id }: any) => (
                        <DeleteRowIcon id={id} onDelete={onDelete} />
                      ),
                    },
                  ]
                : []),
            ]}
            dataFetch={getEmployees}
          />
        </div>
      );
    },
    [currentLocation, refetchTableData, selectedEmployeeId, isAllowWrite]
  );

  if (!currentLocation?.id)
    return <Title order={3}>Please Select Location from Header</Title>;
  return (
    <>
      {isAllowWrite && <Button onClick={CreateTimeLogModal}>Create +</Button>}
      <Select
        my={20}
        w={"30%"}
        data={
          data?.employeeMany?.map((val) => ({
            value: val?.id,
            label: `${val?.user?.firstName}  ${val?.user?.lastName}`,
          })) || []
        }
        label="Select Employee"
        value={selectedEmployeeId}
        onChange={(e) => {
          setSelectedEmployeeId(e);
        }}
      />
      <ShowTable theme={theme} />
    </>
  );
};

export default TimeClockList;
